"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createApprovalApi = void 0;
const react_1 = require("@reduxjs/toolkit/query/react");
/**
 * Approval API factory — see createTicketApi.ts for the shared-package baseQuery-injection
 * pattern (typed against `fetchBaseQuery`'s concrete return type to preserve error/meta
 * inference). Cached separately from the workflow api so invalidating an approval doesn't also
 * throw away the (much larger) workflow definition cache.
 */
const scopeParams = (params) => {
    var _a;
    return ({
        scope: (_a = params === null || params === void 0 ? void 0 : params.scope) !== null && _a !== void 0 ? _a : 'mine',
    });
};
/** Shared `query`/`invalidatesTags` shape for the ApproveStep/RejectStep mutations, which are
 * otherwise identical apart from the endpoint URL. */
const buildStepActionEndpoint = (url) => ({
    query: (req) => ({
        url,
        method: 'POST',
        body: {
            instanceId: req.instanceId,
            stepId: req.stepId,
            approvalRequestId: req.approvalRequestId,
            comment: req.comment,
        },
    }),
    invalidatesTags: (_r, _e, req) => [
        { type: 'Approval', id: req.approvalRequestId },
        'PendingApprovals',
        'MyApprovals',
    ],
});
const createApprovalApi = (baseQuery) => (0, react_1.createApi)({
    reducerPath: 'approvalApi',
    baseQuery,
    tagTypes: ['Approval', 'PendingApprovals', 'MyApprovals'],
    endpoints: (builder) => ({
        // GET /PendingApprovalsCount?scope=mine|all
        // Cheap count-only endpoint — used by the nav-bar badge so the 60s poll
        // doesn't pull (and hydrate) every pending request.
        getPendingApprovalsCount: builder.query({
            query: (params) => ({ url: 'PendingApprovalsCount', params: scopeParams(params) }),
            providesTags: ['PendingApprovals'],
        }),
        // GET /PendingApprovals?scope=mine|all
        // Full pending list for the inbox — includes ticket title/description hydration.
        getPendingApprovals: builder.query({
            query: (params) => ({ url: 'PendingApprovals', params: scopeParams(params) }),
            providesTags: (result) => [
                'PendingApprovals',
                ...(result !== null && result !== void 0 ? result : []).map((a) => ({ type: 'Approval', id: a.id })),
            ],
        }),
        // GET /MyApprovals?scope=mine|all&status=pending|approved|rejected|expired|all
        // Full history view — backs the inbox status-filter chips.
        getMyApprovals: builder.query({
            query: (params) => {
                var _a;
                return ({
                    url: 'MyApprovals',
                    params: Object.assign(Object.assign({}, scopeParams(params)), { status: (_a = params === null || params === void 0 ? void 0 : params.status) !== null && _a !== void 0 ? _a : 'all' }),
                });
            },
            providesTags: (result) => [
                'MyApprovals',
                ...(result !== null && result !== void 0 ? result : []).map((a) => ({ type: 'Approval', id: a.id })),
            ],
        }),
        // GET /ApprovalRequestById?id=...
        // Powers the deep-link landing page — the URL only carries the approval-request id,
        // and this endpoint resolves the parent WorkflowInstance + StepId for the action calls.
        getApprovalRequest: builder.query({
            query: (id) => ({
                url: 'ApprovalRequestById',
                params: { id },
            }),
            providesTags: (_result, _error, id) => [{ type: 'Approval', id }],
        }),
        // POST /ApproveStep — all fields in body
        approveStep: builder.mutation(buildStepActionEndpoint('ApproveStep')),
        // POST /RejectStep — all fields in body
        rejectStep: builder.mutation(buildStepActionEndpoint('RejectStep')),
    }),
});
exports.createApprovalApi = createApprovalApi;
